// Utility helpers to work with semantic version strings (major.minor.patch).
// The compareVersions function returns 1 when the first version is newer,
// -1 when it is older and 0 when both versions are equal. Invalid segments
// default to zero to keep the comparison resilient to partial values.
export function compareVersions(a: string, b: string): number {
  const normalize = (version: string): number[] => {
    const sanitized = version.trim().replace(/^[^0-9]+/, '');

    const toNumericSegment = (segment: string): number => {
      const cleaned = segment.trim().replace(/^[^0-9]+/, '').replace(/[^0-9].*$/, '');
      if (cleaned.length === 0) {
        return 0;
      }
      const parsed = Number.parseInt(cleaned, 10);
      return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    };

    return sanitized
      .split('.')
      .slice(0, 3)
      .map(toNumericSegment)
      .concat([0, 0, 0])
      .slice(0, 3);
  };

  const [aMajor, aMinor, aPatch] = normalize(a);
  const [bMajor, bMinor, bPatch] = normalize(b);

  if (aMajor !== bMajor) {
    return aMajor > bMajor ? 1 : -1;
  }
  if (aMinor !== bMinor) {
    return aMinor > bMinor ? 1 : -1;
  }
  if (aPatch !== bPatch) {
    return aPatch > bPatch ? 1 : -1;
  }
  return 0;
}

export function isVersionGreater(a: string, b: string): boolean {
  return compareVersions(a, b) > 0;
}

export function sortVersionsDescending<T extends { version: string }>(versions: T[]): T[] {
  return [...versions].sort((left, right) => compareVersions(right.version, left.version));
}
